// TODO: use centralized utils between here and activities-desking

// externals
import { dateUtils, formatUtils } from '@makemydeal/dr-common-utils';
import numeral from 'numeral';
import { DATE_FORMAT, MONEY_FACTOR_TYPE, SKEW_DELIMETER } from '../constants';
import type { OfferType } from '@makemydeal/dr-platform-types';
import type { ScenarioReference } from '@makemydeal/dr-dash-bff-types';
import { RequestType, ExtendedRequestType } from '@makemydeal/dr-platform-types';
import { DateTime } from 'luxon';
import { utils } from '@makemydeal/dr-offer-redux';

export const convertToNumber = (dollars: string): number => Number(dollars.replace(/[^0-9.]/g, ''));

export const convertToNumberOrNull = (dollars: string): number | null => {
    const normalized = dollars.replace(/[^0-9.]/g, '').trim();
    if (normalized === '') return null;

    const value = Number(normalized);
    return Number.isFinite(value) ? value : null;
};

export const isNaNish = (text: string): boolean => /^[\D]+$/.test(text) || text.trim() === ''; // Test for only non-numerics

export const formatMileageCharge = (amount: number): string => {
    return numeral(amount).format('0,0.0[00]');
};

export const formatAprRate = (amount: number): string => {
    return numeral(amount).format('0,0.000');
};

export const formatMoneyFactor = (amount: number): string => {
    return numeral(amount).format('0,0.00000');
};

export const getConvertedRateText = (rateType: string, value: number, isBuyRate: boolean): string => {
    const isMoneyFactorType = rateType === MONEY_FACTOR_TYPE;
    const targetRateType = isMoneyFactorType ? 'APR' : MONEY_FACTOR_TYPE;
    const { transformedBuyRate, transformedSellRate } = isBuyRate
        ? utils.convertRatesByType(targetRateType, value, 0)
        : utils.convertRatesByType(targetRateType, 0, value);
    const rate = (isBuyRate ? transformedBuyRate : transformedSellRate) || 0;
    return isMoneyFactorType ? `${formatAprRate(rate)}% in APR` : `${formatMoneyFactor(rate)} in MF`;
};

export const formatPayment = (payment: number) => {
    const formattedDollar = formatUtils.formatDollars(payment);
    const numeralFormat = numeral(formattedDollar).format('0,0.00');
    return numeralFormat;
};

export const skewWords = (words: string[], delimiter: string = SKEW_DELIMETER): string => {
    return words.filter((w) => w?.trim()?.length).join(delimiter);
};

export const formatValue = (label: string, value: number | string): string => {
    if (label === 'APR') {
        return `${value}%`;
    } else if (
        ['Down Payment', 'Selling Price', 'Trade 1 Allowance', 'Total Taxes and Fees', 'Total Add Ons', 'Payment'].includes(label)
    ) {
        return `${value}`;
    }
    return String(value);
};
export const getDisplayOfferType = (offerType: OfferType) => {
    const offerTypesForDisplay = {
        cash: RequestType.CASH,
        finance: RequestType.FINANCE,
        lease: RequestType.LEASE,
        balloon: ExtendedRequestType.BALLOON
    };

    return offerTypesForDisplay[offerType] || RequestType.FINANCE;
};

export const showPlaceholder = (value: number | string) => {
    if (!value) return 'N/A';
    return value;
};

export const getScenarioIdsByDealVersion = (scenarios?: ScenarioReference[], dealVersion?: string): string[] => {
    if (!scenarios || !dealVersion) {
        return [];
    }

    return scenarios.map((scenario) => scenario.id).filter((id) => id !== dealVersion);
};

export const formatPhoneNumber = (phoneNumberString: string) => {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return null;
};

export const toProperCase = (str: string | undefined): string => {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

export const getMidDayUTCTime = (inputDate: string | Date, dateFormat: string = DATE_FORMAT) => {
    if (!inputDate) return null;
    const date: string = typeof inputDate === 'object' ? dateUtils.formatAsMMDDYYYY(inputDate as Date) : (inputDate as string);

    let parsedDate;

    const dateTimeFromFormatString = DateTime.fromFormat(date, dateFormat as string, { zone: 'utc' });

    if (dateTimeFromFormatString.isValid) {
        // when dateTimeFromFormatString is in MM/dd/yyyy
        parsedDate = dateTimeFromFormatString;
    } else {
        // when dateTimeFromFormatString is in ISO format
        parsedDate = DateTime.fromISO(date, { zone: 'utc' });
    }
    return parsedDate.set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).toISO();
};

export const mergeDateWithCurrentUTCTime = (date: string, dateFormat: string) => {
    if (!date) return null;

    let parsedDate;

    const dateTimeFromFormatString = DateTime.fromFormat(date, dateFormat, { zone: 'utc' });

    if (dateTimeFromFormatString.isValid) {
        // when selectedMilestoneDate is in MM/dd/yyyy
        parsedDate = dateTimeFromFormatString;
    } else {
        // when selectedMilestoneDate is in ISO format
        parsedDate = DateTime.fromISO(date, { zone: 'utc' });
    }

    const { hour, minute, second, millisecond } = DateTime.utc();

    return parsedDate.set({ hour, minute, second, millisecond }).toISO();
};

export const mergeDateWithCurrentESTTime = (date: string, dateFormat: string) => {
    if (!date) return null;

    // Strip time and timezone info from ISO strings to get just the date part
    const cleanDate = date.includes('T') ? date.split('T')[0] : date;

    let parsedDate;

    const dateTimeFromFormatString = DateTime.fromFormat(cleanDate, dateFormat, { zone: 'America/New_York' });

    if (dateTimeFromFormatString.isValid) {
        // when selectedMilestoneDate is in yyyy-MM-dd
        parsedDate = dateTimeFromFormatString;
    } else {
        // when selectedMilestoneDate is in ISO format
        parsedDate = DateTime.fromISO(cleanDate, { zone: 'America/New_York' });
    }

    const { hour, minute, second, millisecond } = DateTime.now().setZone('America/New_York');

    return parsedDate.set({ hour, minute, second, millisecond }).toISO();
};

export const formatDecimalPlaces = (value: number | undefined): string | undefined => {
    return value !== undefined ? value.toFixed(2) : undefined;
};

export const formatExportDate = (isoDateStr: string): string => {
    const dt = DateTime.fromISO(isoDateStr).toLocal();
    return dt.isValid ? dt.toFormat('EEE, MMM d yyyy') : '';
};

export const formatExportTime = (isoDateStr: string): string => {
    const dt = DateTime.fromISO(isoDateStr).toLocal();
    return dt.isValid ? dt.toFormat('h:mm a') : '';
};
